"use client";

import { createContext, useContext, useEffect, useState, useRef, ReactNode, useMemo, useCallback } from "react";
import { dataService } from "@/lib/data-service";
import { fetchClients, fetchProducts, fetchInvoices, fetchQuotes, fetchSocietes, createSociete as createSocieteAction, updateSociete as updateSocieteAction, getSociete, updateOverdueInvoices, fetchUserById, markHistoryAsRead, fetchAllUsers, fetchBootData } from "@/app/actions";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal";
import { Societe, Facture, Client, Produit, Devis, User } from "@/types";
import { useRouter } from "next/navigation";
import { useTransition } from "react";

export interface ConfirmOptions {
    title: string;
    message: string;
    onConfirm: () => void;
}

interface DataContextType {
    clients: Client[];
    products: Produit[];
    invoices: Facture[];
    quotes: Devis[];
    societe: Societe | null;
    societes: Societe[];
    user: any;
    switchSociete: (id: string) => void;
    createSociete: (nom: string, details?: any, options?: { skipRedirect?: boolean }) => Promise<any>;
    updateSociete: (societe: Societe) => Promise<any>;
    refreshData: (silent?: boolean) => Promise<void>;
    isLoading: boolean;
    isDirty: boolean;
    setIsDirty: (dirty: boolean) => void;
    history: any[];
    logAction: (action: 'create' | 'update' | 'delete' | 'read' | 'other', entityType: 'facture' | 'devis' | 'client' | 'produit' | 'societe' | 'settings', description: string, entityId?: string) => void;
    confirm: (options: ConfirmOptions) => void;
    markHistoryAsRead: () => Promise<void>;
    isSwitching: boolean;
    // Optimistic Updates Helpers
    addInvoice: (invoice: Facture) => void;
    updateInvoiceInList: (invoice: Facture) => void;
    removeInvoice: (id: string) => void;
    dashboardMetrics: any;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: ReactNode }) {
    const router = useRouter();
    const [clients, setClients] = useState<Client[]>([]);
    const [products, setProducts] = useState<Produit[]>([]);
    const [invoices, setInvoices] = useState<Facture[]>([]);
    const [quotes, setQuotes] = useState<Devis[]>([]);
    const [societe, setSociete] = useState<Societe | null>(null);
    const [societes, setSocietes] = useState<Societe[]>([]);
    const [user, setUser] = useState<any>(null);
    const [history, setHistory] = useState<any[]>([]);

    const [authChecked, setAuthChecked] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSwitching, setIsSwitching] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [dashboardMetrics, setDashboardMetrics] = useState<any>(null);

    // PERFORMANCE COUNTERS
    const renderCount = useRef(0);
    renderCount.current++;

    const [isPending, startTransition] = useTransition();
    const fetchingRef = useRef(false);
    const fetchIdRef = useRef(0);
    const lastFetchRef = useRef<number>(0);
    const lastHistoryFetchRef = useRef<Record<string, number>>({});

    const fetchData = useCallback(async (silent: boolean = false, targetSocieteId?: string) => {
        const now = Date.now();
        // Prevent concurrent fetches and rapid successive fetches (1s throttle)
        if (fetchingRef.current && !targetSocieteId) return;
        if (!targetSocieteId && !silent && (now - lastFetchRef.current < 1000)) return;

        // Use direct window pathname to avoid re-renders on every navigation
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
        if (currentPath?.startsWith('/login') || currentPath?.startsWith('/signup')) {
            setIsLoading(false);
            setAuthChecked(true); 
            return;
        }

        fetchingRef.current = true;
        lastFetchRef.current = now;
        const currentFetchId = ++fetchIdRef.current;

        try {
            // Set switching state IMMEDIATELY and synchronously before await
            if (targetSocieteId) setIsSwitching(true);
            if (!silent && !targetSocieteId) setIsLoading(true);

            if (process.env.NODE_ENV === 'development') {
                console.time(`[PERF] API:fetchBootData:${targetSocieteId || 'current'}`);
            }

            const startTime = performance.now();
            const res = await fetchBootData(targetSocieteId);
            const apiDuration = performance.now() - startTime;

            // Check if this request is still relevant
            if (currentFetchId !== fetchIdRef.current) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn(`[PERF] API call ignored (stale): id=${currentFetchId}`);
                }
                return;
            }

            if (process.env.NODE_ENV === 'development') {
                console.timeEnd(`[PERF] API:fetchBootData:${targetSocieteId || 'current'}`);
                // Estimate payload size (rough)
                const payloadStr = JSON.stringify(res.data || {});
                const payloadKB = (payloadStr.length / 1024).toFixed(1);
                console.log(`[PERF] Payload Size: ~${payloadKB} KB`);
            }

            if (res.success && res.data) {
                const { user: bootUser, societes: bootSocietes, activeSociete: bootActiveSoc, clients: bootClients, products: bootProducts, dashboard } = res.data;
                
                if (process.env.NODE_ENV === 'development') {
                    console.groupCollapsed(`[PERF] Data Update (Transition) - API took ${apiDuration.toFixed(1)}ms`);
                }
                const transitionStart = performance.now();

                // Group all state updates into ONE transition to minimize re-renders
                startTransition(() => {
                    if (bootUser) {
                        setUser(bootUser);
                    }
                    
                    setAuthChecked(true);
                    setSocietes(bootSocietes || []);
                    setSociete(bootActiveSoc);
                    setClients(bootClients || []);
                    setProducts(bootProducts || []);

                    // SYNC Cache: Force LocalStorage update to match DB Fetch
                    // This fixes the issue where DataService/PDFSettings read from STALE cache
                    if (res.data) {
                        dataService.syncFromBootData(res.data);
                    }
                    
                    if (dashboard) {
                        setInvoices(dashboard.invoices || []);
                        setQuotes(dashboard.quotes || []);
                        setDashboardMetrics(dashboard.summaryMetrics || null);
                    }

                    const activeId = bootActiveSoc?.id;
                    if (activeId) {
                        if (typeof window !== 'undefined') {
                            localStorage.setItem("glassy_active_societe_id", activeId);
                        }
                        const nowHistory = Date.now();
                        if (!lastHistoryFetchRef.current[activeId] || (nowHistory - lastHistoryFetchRef.current[activeId] > 30000)) {
                            dataService.getHistory(15, activeId).then(setHistory);
                            lastHistoryFetchRef.current[activeId] = nowHistory;
                        }
                    }
                });
            } else if (res.error === "Non authentifié") {
                const pathAtError = typeof window !== 'undefined' ? window.location.pathname : '';
                if (pathAtError !== "/login" && pathAtError !== "/signup") {
                    router.push("/login");
                }
            }
        } catch (error) {
            console.error("[DATA_PROVIDER] Boot failed:", error);
        } finally {
            // Delay closing loaders slightly for smoother visual transition
            setTimeout(() => {
                setIsLoading(false);
                setIsSwitching(false);
                fetchingRef.current = false;
            }, 100);
        }
    }, [router]);

    // Initial load - stable dependency on fetchData
    useEffect(() => {
        dataService.initialize();
        if (!authChecked) {
            fetchData();
        }
    }, [authChecked, fetchData]);

    // Confirmation Modal State
    const [confirmState, setConfirmState] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    }>({
        isOpen: false,
        title: "",
        message: "",
        onConfirm: () => { }
    });

    const confirm = useCallback((options: ConfirmOptions) => {
        setConfirmState({
            isOpen: true,
            title: options.title,
            message: options.message,
            onConfirm: () => {
                options.onConfirm();
                setConfirmState(prev => ({ ...prev, isOpen: false }));
            }
        });
    }, []);

    const handleRefresh = useCallback(async (silent: boolean = false) => {
        await fetchData(silent);
    }, [fetchData]);

    const handleSwitchSociete = useCallback(async (societeId: string) => {
        const selectedSoc = societes.find(s => s.id === societeId);
        
        const executeSwitch = () => {
            // We NO LONGER clear data or setSociete here.
            // We wait for fetchData to finish and update EVERYTHING in one transition.
            dataService.switchSociete(societeId);
            fetchData(true, societeId); 
        };

        if (isDirty) {
            confirm({
                title: "Modifications non enregistrées",
                message: "Des modifications non enregistrées seront perdues. Continuer ?",
                onConfirm: () => {
                    setIsDirty(false);
                    executeSwitch();
                }
            });
            return;
        }

        executeSwitch();
    }, [isDirty, confirm, fetchData, societes]);

    const handleCreateSociete = useCallback(async (nom: string, details?: any, options?: { skipRedirect?: boolean }) => {
        setIsLoading(true);
        const res = await createSocieteAction(nom, details);
        if (res.success && res.data) {
            const newId = (res.data as any).id;
            dataService.switchSociete(newId);
            fetchData();
            if (!options?.skipRedirect) {
                router.push("/settings");
            }
            setIsLoading(false);
            return res.data;
        } else {
            console.error(res.error);
            setIsLoading(false);
            return null;
        }
    }, [fetchData, router]);

    const handleUpdateSociete = useCallback(async (data: Societe) => {
        // Optimistic update of the list and current societe
        setSociete(prev => prev?.id === data.id ? { ...prev, ...data } : prev);
        setSocietes(prev => prev.map(s => s.id === data.id ? { ...s, ...data } : s));

        const res = await updateSocieteAction(data);
        if (res.success) {
            fetchData(true); // Silent refresh
        } else {
            console.error(res.error);
        }
        return res;
    }, [fetchData]);

    const handleMarkHistoryRead = useCallback(async () => {
        const currentUser = user || dataService.getCurrentUser();
        if (currentUser && currentUser.id) {
            await markHistoryAsRead(currentUser.id);
            setUser((prev: any) => prev ? { ...prev, lastReadHistory: new Date().toISOString() } : null);
        }
    }, [user]);

    const handleLogAction = useCallback(async (action: any, entityType: any, description: string, entityId?: string) => {
        const currentUser = user || dataService.getCurrentUser();
        if (currentUser) {
            await dataService.logAction(currentUser, action, entityType, description, entityId);
            const currentSocieteId = societe?.id;
            if (currentSocieteId) {
                const newHistory = await dataService.getHistory(15, currentSocieteId);
                setHistory(newHistory);
            }
        }
    }, [user, societe?.id]);

    /**
     * UNE FACTURE SANS IDENTIFIANT N'ENTRE PAS DANS LA LISTE.
     *
     * L'éditeur y a inséré, à la création, l'objet du FORMULAIRE — dont l'« id »
     * vaut « undefined », puisque le document n'existait pas encore. Tout ce qui
     * part ensuite de la liste s'adresse au serveur par cet identifiant :
     * l'aperçu rendait « Erreur lors de la génération de l'aperçu », le
     * téléchargement et l'envoi tombaient de même, et la ligne menait à
     * « /factures/undefined ». Un rechargement effaçait le symptôme, ce qui l'a
     * rendu difficile à cerner.
     *
     * L'appelant est corrigé ; le garde est ici pour que la question ne se
     * repose pas. Il refuse plutôt que d'insérer une entrée qui ne mène nulle
     * part, et il le dit — « error » et « warn » sont les deux seuls niveaux que
     * « removeConsole » laisse passer en production.
     */
    const handleAddInvoice = useCallback((inv: Facture) => {
        if (!inv?.id) {
            console.error("[Factures] insertion refusée : la facture n'a pas d'identifiant", inv?.numero);
            return;
        }
        setInvoices(prev => [inv, ...prev]);
    }, []);
    const handleUpdateInvoiceInList = useCallback((inv: Facture) => {
        if (!inv?.id) {
            console.error("[Factures] mise à jour refusée : la facture n'a pas d'identifiant", inv?.numero);
            return;
        }
        setInvoices(prev => prev.map(item => item.id === inv.id ? inv : item));
    }, []);
    const handleRemoveInvoice = useCallback((id: string) => setInvoices(prev => prev.filter(item => item.id !== id)), []);

    // Sync visibility
    useEffect(() => {
        const handleSync = () => {
            const now = Date.now();
            if (document.visibilityState === 'visible' && user && societe && (now - lastFetchRef.current > 300000)) {
                fetchData(true);
            }
        };
        window.addEventListener("focus", handleSync);
        window.addEventListener("visibilitychange", handleSync);
        return () => {
            window.removeEventListener("focus", handleSync);
            window.removeEventListener("visibilitychange", handleSync);
        };
    }, [user, societe, fetchData]);

    // LE DÉCLENCHEUR NAVIGATEUR A ÉTÉ RETIRÉ. Il appelait
    // « /api/cron/process-scheduled-emails » toutes les soixante secondes,
    // depuis le poste de quiconque avait l'application ouverte.
    //
    // L'expédition des courriels programmés était donc pilotée par un onglet.
    // Combiné à la garde de cette route — qui, faute de CRON_SECRET, était
    // sautée et laissait donc passer TOUT LE MONDE —, cela donne ce que
    // montrait le journal nginx du 30 août 2026 : 1 024 exécutions en 200,
    // toutes déclenchées de l'extérieur, aucune authentifiée.
    //
    // L'unique déclencheur est désormais la crontab du serveur, toutes les
    // cinq minutes, porteuse de CRON_SECRET. Un client ne déclenche pas un
    // envoi : il n'a rien à en savoir, et sa présence n'est pas un événement
    // métier.

    /**
     * PRÉCHARGEMENT DE LA POLICE UNICODE.
     *
     * « generateInvoicePDF » est SYNCHRONE — une quinzaine d'écrans l'appellent
     * ainsi, et la rendre asynchrone les casserait tous. Le chargement d'une
     * police, lui, ne peut pas l'être. On le déclenche donc ici, en amont.
     *
     * La condition compte autant que l'effet : la police pèse 757 Ko et ne sert
     * qu'aux fiches dont l'écriture n'est pas latine. La télécharger pour les
     * comptes qui n'en ont aucune — c'est-à-dire presque tous — serait un coût
     * pur. Le préchargement n'est donc lancé QUE si la société en compte au
     * moins une.
     *
     * S'il échoue ou n'a pas eu le temps d'aboutir, le PDF reste correct : il
     * imprime les romanisations rangées en base, et annonce les rares champs
     * qu'il n'a pas su écrire. Rien ici n'est bloquant.
     */
    useEffect(() => {
        const aDuNonLatin = clients.some(
            (c) => c.scriptOriginal && c.scriptOriginal !== 'latin'
        );
        if (!aDuNonLatin) return;

        let annule = false;
        import('@/lib/pdf-polices').then(({ chargerPoliceUnicode }) => {
            if (!annule) void chargerPoliceUnicode();
        });
        return () => { annule = true; };
    }, [clients]);

    // Before unload warning
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    const contextValue = useMemo(() => ({
        clients, products, invoices, quotes, societe, societes, user,
        switchSociete: handleSwitchSociete,
        refreshData: handleRefresh,
        createSociete: handleCreateSociete,
        updateSociete: handleUpdateSociete,
        isLoading, isDirty, setIsDirty, history,
        logAction: handleLogAction,
        confirm,
        markHistoryAsRead: handleMarkHistoryRead,
        isSwitching,
        addInvoice: handleAddInvoice,
        updateInvoiceInList: handleUpdateInvoiceInList,
        removeInvoice: handleRemoveInvoice,
        dashboardMetrics
    }), [
        clients, products, invoices, quotes, societe, societes, user,
        handleSwitchSociete, handleRefresh, handleCreateSociete, handleUpdateSociete,
        isLoading, isDirty, history,
        handleLogAction, confirm, handleMarkHistoryRead, isSwitching,
        handleAddInvoice, handleUpdateInvoiceInList, handleRemoveInvoice,
        dashboardMetrics
    ]);

    return (
        <DataContext.Provider value={contextValue}>
            {children}
            <ConfirmationModal
                isOpen={confirmState.isOpen}
                onClose={() => setConfirmState(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmState.onConfirm}
                title={confirmState.title}
                message={confirmState.message}
            />
        </DataContext.Provider>
    );
}

export const useData = () => {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error("useData must be used within a DataProvider");
    }
    return context;
};
